import { useCallback, useEffect, useMemo, useState } from 'react'
import { CloudDownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { message } from 'antd'
import { useNovelCoreStore } from '@/stores/novelCoreStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { isThesisCloudAuth } from '@/lib/thesisApi'
import type { ThesisCloudProjectMeta } from '@/lib/thesisCloudApi'
import {
  fetchCloudProjectList,
  restoreCloudProjectToLocal,
} from '@/lib/thesisCloudRestore'
import { useRouter } from '@/router/hooks'

/** 换设备登录后：从云端下载已保存论文到本机 */
export default function ThesisCloudRestore() {
  const token = useSubscriptionStore((s) => s.token)
  const novels = useNovelCoreStore((s) => s.novels)
  const router = useRouter()
  const [projects, setProjects] = useState<ThesisCloudProjectMeta[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [err, setErr] = useState('')

  const localIds = useMemo(() => new Set(novels.map((n) => n.id)), [novels])

  const refresh = useCallback(async () => {
    if (!token || !isThesisCloudAuth()) {
      setProjects([])
      return
    }
    setLoading(true)
    setErr('')
    const r = await fetchCloudProjectList()
    setLoading(false)
    if (!r.ok) {
      setErr(r.error)
      setProjects([])
      return
    }
    setProjects(r.projects)
  }, [token])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (!token || !isThesisCloudAuth()) return null

  const missing = projects.filter((p) => !localIds.has(p.localNovelId))
  const present = projects.filter((p) => localIds.has(p.localNovelId))

  const downloadOne = async (p: ThesisCloudProjectMeta, openAfter = false) => {
    setBusyId(p.id)
    try {
      const r = await restoreCloudProjectToLocal(p.id)
      if (!r.ok) {
        message.error(r.error)
        return
      }
      message.success(`已下载「${r.title}」`)
      await refresh()
      if (openAfter) router.push(`/papers/${r.localNovelId}/manuscript`)
    } finally {
      setBusyId(null)
    }
  }

  const downloadAllMissing = async () => {
    if (!missing.length) return
    setLoading(true)
    let ok = 0
    for (const p of missing) {
      const r = await restoreCloudProjectToLocal(p.id)
      if (r.ok) ok += 1
    }
    setLoading(false)
    message.success(`已下载 ${ok}/${missing.length} 篇`)
    await refresh()
  }

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('zh-CN', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  if (!loading && !err && projects.length === 0) return null

  return (
    <section className="thesis-cloud-restore mb-6 rounded-lg border border-[var(--tt-line,rgba(0,0,0,.08))] bg-[var(--tt-panel,#fafafa)] p-4 dark:bg-[rgba(255,255,255,.03)]">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="font-semibold text-sm flex items-center gap-1.5">
            <CloudDownloadOutlined />
            云端论文
          </p>
          <p className="text-xs thesis-muted mt-0.5">
            换设备登录后，点「下载到本机」才能在本机打开已保存内容
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            className="thesis-btn thesis-btn--ghost text-sm"
            disabled={loading}
            onClick={() => void refresh()}
          >
            <ReloadOutlined />
            刷新
          </button>
          {missing.length > 0 ? (
            <button
              type="button"
              className="thesis-btn thesis-btn--ghost text-sm"
              disabled={loading || !!busyId}
              onClick={() => void downloadAllMissing()}
            >
              <CloudDownloadOutlined />
              全部下载（{missing.length}）
            </button>
          ) : null}
        </div>
      </div>

      {err ? <p className="text-sm text-red-500 mb-2">{err}</p> : null}
      {loading && !projects.length ? (
        <p className="text-sm thesis-muted">正在查询云端…</p>
      ) : null}

      {missing.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {missing.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 rounded-md bg-[var(--color-bg-container,#fff)] dark:bg-[rgba(0,0,0,.2)] px-3 py-2.5"
            >
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{p.title || '未命名论文'}</div>
                <div className="text-xs thesis-muted">云端更新 {formatTime(p.updatedAt)}</div>
              </div>
              <button
                type="button"
                className="thesis-btn thesis-btn--ghost text-sm shrink-0"
                disabled={busyId === p.id}
                onClick={() => void downloadOne(p, true)}
              >
                {busyId === p.id ? '下载中…' : '下载到本机'}
              </button>
            </li>
          ))}
        </ul>
      ) : projects.length > 0 ? (
        <p className="text-sm thesis-muted">云端 {projects.length} 篇均已在本机。</p>
      ) : null}

      {present.length > 0 && missing.length > 0 ? (
        <details className="mt-3 text-xs thesis-muted">
          <summary className="cursor-pointer">已在本机的云端篇（{present.length}）</summary>
          <ul className="mt-2 space-y-1 pl-1">
            {present.map((p) => (
              <li key={p.id} className="flex justify-between gap-2">
                <span className="truncate">{p.title}</span>
                <button
                  type="button"
                  className="underline shrink-0"
                  disabled={busyId === p.id}
                  onClick={() => void downloadOne(p, false)}
                >
                  重新覆盖下载
                </button>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  )
}
